import { baseApiAuthenticated } from '@/api/v2/index';
import { CreateWebshop, Webshop } from '@/models/webshop';
import { urlBuilder } from '@/api/utils';

type GetWebshopResponse = {
    liftOperatorConfigurations: Webshop[];
};

const baseUrl = urlBuilder('liftoperators', `configurations`);

export const configurationsApi = baseApiAuthenticated
    .enhanceEndpoints({
        addTagTypes: ['WEBSHOPS'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getConfigurations: builder.query<
                Webshop[],
                { liftOperatorId: string }
            >({
                query: () => baseUrl,
                transformResponse: (
                    { liftOperatorConfigurations }: GetWebshopResponse,
                    _meta,
                    { liftOperatorId }
                ): Webshop[] => {
                    return liftOperatorConfigurations.filter(
                        (lC) => lC.liftOperatorId === liftOperatorId
                    );
                },
                providesTags: ['WEBSHOPS'],
            }),

            putWebshop: builder.mutation<Webshop, Webshop>({
                query: (webshop) => ({
                    method: 'PUT',
                    url: `${baseUrl}/${webshop.id}`,
                    body: webshop,
                }),
                invalidatesTags: ['WEBSHOPS'],
            }),

            postWebshop: builder.mutation<Webshop, CreateWebshop>({
                query: (webshop) => ({
                    method: 'POST',
                    url: baseUrl,
                    body: webshop,
                }),
                invalidatesTags: ['WEBSHOPS'],
            }),

            deleteWebshop: builder.mutation<Webshop, { webshopId: string }>({
                query: ({ webshopId }) => ({
                    method: 'DELETE',
                    url: `${baseUrl}/${webshopId}`,
                }),
                invalidatesTags: ['WEBSHOPS'],
            }),

            putWebshopImage: builder.mutation<
                Webshop,
                { webshopId: string; image: File; type: 'headerImage' | 'logo' }
            >({
                query: ({ webshopId, image, type }) => ({
                    method: 'PUT',
                    url: `${baseUrl}/${webshopId}/${type}`,
                    body: image,
                }),
                invalidatesTags: ['WEBSHOPS'],
            }),

            deleteWebshopImage: builder.mutation<
                Webshop,
                { webshopId: string; type: 'headerImage' | 'logo' }
            >({
                query: ({ webshopId, type }) => ({
                    method: 'DELETE',
                    url: `${baseUrl}/${webshopId}/${type}`,
                }),
                invalidatesTags: ['WEBSHOPS'],
            }),
        }),
    });

export const {
    useGetConfigurationsQuery,
    usePutWebshopMutation,
    usePostWebshopMutation,
    usePutWebshopImageMutation,
    useDeleteWebshopImageMutation,
} = configurationsApi;
